import React, { useState, useEffect } from 'react';
import {
    Card, Button, Table, Modal, Select, DatePicker, message, Spin,
    Space, Tag, Row, Col, Statistic, Popconfirm, Radio, Input,
    Divider, Progress, Badge
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, EyeOutlined,
    UserOutlined, TeamOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import { usePeriods } from '../../hooks/usePeriods';
import FacilityService from '../../services/FacilityService';
import { AntdSelect, AntdDatepicker } from '../../utils/Antd';
import moment from 'moment';
import { trailCreate, trailDelete, trailPageView, TRAIL_MODULES } from '../../utils/activityTrail';
const { TextArea } = Input;

// ─── Score map ───────────────────────────────────────────────────────────────
const SCORE_MAP = {
    'Very Satisfied':100,'Very Good':100,'Very good':100,
    'Satisfied':75,'Good':75,'good':75,
    'Average':50,
    'Unsatisfied':25,'Poor':25,'poor':25,
    'Very Unsatisfied':0,'Very Poor':0,'Very poor':0,
    'Yes':100,'No':0,
    '< 30 Minutes':100,'30min-1 Hour':75,'1 Hour-2 Hours':25,'More than 2 Hours':0,
};

const scoreColor = (s) => {
    if (s>=75) return '#52c41a';
    if (s>=50) return '#faad14';
    if (s>=25) return '#fa8c16';
    return '#f5222d';
};

const SECTION_COLORS = {
    'TIMELINESS':'#0ea5e9',
    'ATTITUDE':'#8b5cf6',
    'INTERPERSONAL RELATIONS/ATTITUDES OF STAFF':'#8b5cf6',
    'FACILITY DIRECTIONS/SIGNAGE AND CLEANLINESS':'#10b981',
    'SUPPLIES AND COMMODITIES':'#f59e0b',
    'OBSERVATION OF PATIENTS RIGHTS':'#ef4444',
    'OVERALL SATISFACTION':'#6366f1',
};

// ─── Inpatient Questions ─────────────────────────────────────────────────────
const IP_QUESTIONS = [
    { ref:'IP.1', section:'TIMELINESS',
      text:'After admission how long did you wait before being allocated a ward and bed?',
      type:'radio', options:['< 30 Minutes','30min-1 Hour','1 Hour-2 Hours','More than 2 Hours'] },
    { ref:'IP.2', section:'ATTITUDE',
      text:'During your stay, how was your interaction with health workers (cadres)?',
      type:'matrix', sub_items:['Nurse','Doctor','Caterers','Laboratory','Pharmacy'],
      options:['Very good','Good','Poor','Very poor'] },
    { ref:'IP.3', section:'FACILITY DIRECTIONS/SIGNAGE AND CLEANLINESS',
      text:'How is signage for directions to service points? (Within and outside the facility)',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'IP.4', section:'FACILITY DIRECTIONS/SIGNAGE AND CLEANLINESS',
      text:'How is the facility cleanliness? (TOILETS, SHOWERS, LINEN, WARD)',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'IP.5', section:'SUPPLIES AND COMMODITIES',
      text:'Did you receive all the prescribed medication during your consultation/visit?',
      type:'yesno' },
    { ref:'IP.6', section:'SUPPLIES AND COMMODITIES',
      text:'Did you receive your meals on time?',
      type:'yesno' },
    { ref:'IP.7', section:'SUPPLIES AND COMMODITIES',
      text:'How was the presentation of the food?',
      type:'radio', options:['Very good','Good','Average','Poor','Very Poor'] },
    { ref:'IP.8', section:'OBSERVATION OF PATIENTS RIGHTS',
      text:'Respect of privacy during your stay (visual/audio)',
      type:'yesno' },
    { ref:'IP.9', section:'OBSERVATION OF PATIENTS RIGHTS',
      text:'Did the health practitioner provide an explanation for your condition, procedures to care and treatment plan?',
      type:'yesno' },
    { ref:'IP.10', section:'OBSERVATION OF PATIENTS RIGHTS',
      text:'Were you aware that you could make a formal complaint in the hospital?',
      type:'yesno' },
    { ref:'IP.11', section:'OVERALL SATISFACTION',
      text:'How satisfactory were the hospital routines and procedures for you?',
      type:'radio', options:['Very Satisfied','Satisfied','Unsatisfied','Very Unsatisfied'] },
];

// ─── Outpatient Questions ────────────────────────────────────────────────────
const OP_QUESTIONS = [
    { ref:'OP.1', section:'TIMELINESS',
      text:'How long did you wait before receiving service from these service points/providers? (consider opening time of the facility 0800hrs)',
      type:'matrix', sub_items:['Screening','Nurse','Doctor','Laboratory','Medical Imaging (X-Ray, scan etc.)','Pharmacy'],
      options:['< 30 Minutes','30min-1 Hour','1 Hour-2 Hours','More than 2 Hours'] },
    { ref:'OP.2', section:'TIMELINESS',
      text:'How would you rate the time management during service delivery in the facility?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.3', section:'INTERPERSONAL RELATIONS/ATTITUDES OF STAFF',
      text:'How was the attitude of the health care provider?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.4', section:'FACILITY DIRECTIONS/SIGNAGE AND CLEANLINESS',
      text:'How is signage for directions to service points? (Within and outside the facility)',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.5', section:'FACILITY DIRECTIONS/SIGNAGE AND CLEANLINESS',
      text:'How is the facility cleanliness?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.6', section:'SUPPLIES AND COMMODITIES',
      text:'Did you receive all the prescribed medication during your consultation/visit?',
      type:'yesno' },
    { ref:'OP.7', section:'OBSERVATION OF PATIENTS RIGHTS',
      text:'Were you treated with respect?',
      type:'yesno' },
    { ref:'OP.8', section:'OBSERVATION OF PATIENTS RIGHTS',
      text:'Did the health practitioner provide an explanation for your condition, procedures to care and treatment plan?',
      type:'yesno' },
    { ref:'OP.9', section:'OBSERVATION OF PATIENTS RIGHTS',
      text:'Were you given clear guidance on follow up care?',
      type:'yesno' },
    { ref:'OP.10', section:'OVERALL SATISFACTION',
      text:'How satisfied were you with the service you received?',
      type:'radio', options:['Very Satisfied','Satisfied','Unsatisfied','Very Unsatisfied'] },
];

// ─── Medication groups ───────────────────────────────────────────────────────
const MEDICATION_GROUPS = {
    'NCD': [
        'Antihypertensives (Blood pressure)','Antidiabetics (Diabetes)','Statins (Cholesterol)',
        'Aspirin / Antiplatelet','Inhaler / Asthma medication','Anticoagulants','Other NCD medication',
    ],
    'HIV': [
        'ARVs — 1st line (TLD, TLE)','ARVs — 2nd line','ARVs — 3rd line',
        'OI Prophylaxis (Cotrimoxazole)','TB Preventive Therapy (TPT/INH)','Other HIV-related medication',
    ],
    'TB': [
        'TB Treatment — Intensive Phase (2RHZE)','TB Treatment — Continuation Phase (4RH)',
        'MDR-TB Regimen','TB Prophylaxis (INH)','Other TB medication',
    ],
    'General': [
        'Analgesics (Pain relief)','Antibiotics','Antifungals','Antiparasitics (Deworming, Antimalarials)',
        'Vitamins / Supplements','Contraceptives','Wound care / Topical','Oral Rehydration Salts (ORS)','Other general medication',
    ],
};
const MED_GROUP_COLORS = { 'NCD':'#ef4444', 'HIV':'#8b5cf6', 'TB':'#f59e0b', 'General':'#10b981' };

// ─── Helper: parse services_offered (handles array or JSON string) ────────────
function parseServices(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return []; }
}

// ─── Shared UI atoms ─────────────────────────────────────────────────────────
const SectionHdr = ({ title }) => (
    <div style={{
        backgroundColor: SECTION_COLORS[title]||'#1890ff', color:'#fff', fontWeight:700, fontSize:12,
        padding:'7px 12px', borderRadius:5, marginTop:18, marginBottom:10, letterSpacing:'0.4px',
    }}>{title}</div>
);

const OptionBtn = ({ label, selected, onClick, color }) => {
    const isGood = ['Very Good','Very good','Very Satisfied','Yes','< 30 Minutes'].includes(label);
    const isBad  = ['Very Poor','Very poor','Very Unsatisfied','No','More than 2 Hours'].includes(label);
    const bg = selected ? (color || (isGood ? '#52c41a' : isBad ? '#f5222d' : '#1890ff')) : '#f0f0f0';
    return (
        <button onClick={onClick} style={{
            padding:'5px 13px', borderRadius:5,
            border: selected ? 'none' : '1px solid #d9d9d9',
            backgroundColor:bg, color: selected ? '#fff' : '#555',
            cursor:'pointer', fontSize:12, fontWeight: selected ? 700 : 400,
            transition:'all 0.18s', margin:'2px',
        }}>{label}</button>
    );
};

const CircleSelect = ({ selected, onClick, color }) => (
    <div onClick={onClick} style={{
        width:22, height:22, borderRadius:'50%',
        border:`2px solid ${selected ? (color||'#1890ff') : '#d9d9d9'}`,
        backgroundColor: selected ? (color||'#1890ff') : 'transparent',
        margin:'0 auto', cursor:'pointer',
        display:'flex', alignItems:'center', justifyContent:'center',
    }}>
        {selected && <div style={{width:10,height:10,borderRadius:'50%',backgroundColor:'#fff'}}/>}
    </div>
);

// ─── SURVEY FORM ──────────────────────────────────────────────────────────────
function SurveyForm({ type, facilities, onSaved, onClose, periods, currentPeriod }) {
    const questions = type === 'inpatient' ? IP_QUESTIONS : OP_QUESTIONS;
    const [hdr, setHdr] = useState({
        survey_date: moment(), period_label: '',
        patient_gender: '', patient_age: '', paid_for_service: '', amount_paid: '',
        fac_services: [], // populated when a facility is selected
    });

    useEffect(() => {
        if (currentPeriod) setHdr(h => ({ ...h, period_label: currentPeriod.label }));
    }, [currentPeriod]);

    const [resp, setRespState] = useState({});
    const [comments, setComments] = useState('');
    const [loading, setLoading] = useState(false);
    const isIP = type === 'inpatient';

    const setR = (ref, val, sub=null) => {
        const k = sub ? `${ref}__${sub}` : ref;
        setRespState(p => ({...p, [k]: val}));
    };
    const getR = (ref, sub=null) => resp[sub ? `${ref}__${sub}` : ref];

    const pct = () => {
        let tot=0, done=0;
        questions.forEach(q => {
            if (q.type==='matrix') q.sub_items.forEach(s => { tot++; if (getR(q.ref,s)) done++; });
            else { tot++; if (getR(q.ref)) done++; }
        });
        return tot ? Math.round((done/tot)*100) : 0;
    };

    const payload = () => {
        const arr=[];
        questions.forEach(q => {
            if (q.type==='matrix') {
                q.sub_items.forEach(s => { const v=getR(q.ref,s); if(v) arr.push({question_ref:q.ref,sub_item:s,response_value:v}); });
            } else {
                const v=getR(q.ref);
                if(v) arr.push({question_ref:q.ref,sub_item:null,response_value:v});
                if (v === 'No' && (q.text.toLowerCase().includes('medication') || q.text.toLowerCase().includes('prescribed'))) {
                    Object.keys(MEDICATION_GROUPS).forEach(grp => {
                        const key = `${q.ref}__notreceived__${grp}`;
                        const meds = getR(key);
                        if (Array.isArray(meds) && meds.length > 0) {
                            arr.push({ question_ref: key, sub_item: grp, response_value: meds.join('|') });
                        }
                    });
                }
            }
        });
        return arr;
    };

    const save = async () => {
        if (!hdr.facility_id) { message.error('Please select a facility'); return; }
        const fac = facilities.find(f=>f.id===hdr.facility_id);
        setLoading(true);
        try {
            await QoCService.saveSurvey({
                ...hdr,
                survey_type: type,
                period_label: hdr.period_label || currentPeriod?.label || '',
                survey_date: moment(hdr.survey_date).format('YYYY-MM-DD'),
                facility_name: fac?.name||'',
                region: fac?.region||'',
                patient_gender:   hdr.patient_gender   || '',
                patient_age:      hdr.patient_age       || '',
                paid_for_service: hdr.paid_for_service  || '',
                amount_paid:      hdr.paid_for_service === 'Yes' ? (hdr.amount_paid || '') : '',
                comments,
                responses: payload(),
            });
            const facName = facilities.find(f => f.id === hdr.facility_id)?.name || String(hdr.facility_id);
            trailCreate(TRAIL_MODULES.SURVEY, type === 'inpatient' ? 'Inpatient Survey' : 'Outpatient Survey', '',
                { facilityName: facName, period: hdr.period_label });
            message.success('Survey saved');
            onSaved();
        } catch { message.error('Failed to save survey'); }
        finally { setLoading(false); }
    };

    const completion = pct();
    const accentColor = isIP ? '#003580' : '#006837';
    const sections = [...new Set(questions.map(q=>q.section))];

    const renderQuestion = (q) => {
        const sColor = SECTION_COLORS[q.section]||'#1890ff';
        const qNum = q.ref.split('.')[1];

        if (q.type === 'yesno') {
            const isMedQuestion = q.text.toLowerCase().includes('medication') || q.text.toLowerCase().includes('prescribed');
            const answeredNo = getR(q.ref) === 'No';
            return (
                <Card key={q.ref} size="small" style={{ marginBottom:8, borderLeft:`3px solid ${sColor}` }}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>
                        <Tag color={sColor} style={{border:'none',color:'#fff',backgroundColor:sColor}}>Q{qNum}</Tag> {q.text}
                    </div>
                    <div style={{display:'flex',gap:10}}>
                        {['Yes','No'].map(opt => <OptionBtn key={opt} label={opt} selected={getR(q.ref)===opt} onClick={()=>setR(q.ref,opt)} />)}
                    </div>
                    {isMedQuestion && answeredNo && (
                        <div style={{ marginTop:12, padding:'10px 12px', background:'#fff7ed', borderRadius:8, border:'1px solid #fed7aa' }}>
                            <div style={{ fontWeight:700, fontSize:11, color:'#c2410c', marginBottom:10 }}>
                                ⚠ Which medication(s) were not received? (select all that apply)
                            </div>
                            {Object.entries(MEDICATION_GROUPS).map(([group, meds]) => {
                                const groupKey = `${q.ref}__notreceived__${group}`;
                                const selected = getR(groupKey) || [];
                                const groupColor = MED_GROUP_COLORS[group] || '#888';
                                return (
                                    <div key={group} style={{ marginBottom:10 }}>
                                        <div style={{ fontWeight:700, fontSize:11, color: groupColor, marginBottom:5, display:'flex', alignItems:'center', gap:6 }}>
                                            <span style={{ width:8, height:8, borderRadius:'50%', background:groupColor, display:'inline-block' }}/>
                                            {group}
                                        </div>
                                        <div style={{ display:'flex', flexWrap:'wrap', gap:4, paddingLeft:14 }}>
                                            {meds.map(med => {
                                                const isChecked = Array.isArray(selected) && selected.includes(med);
                                                return (
                                                    <div key={med}
                                                        onClick={() => {
                                                            const cur = Array.isArray(getR(groupKey)) ? getR(groupKey) : [];
                                                            const next = isChecked ? cur.filter(m => m !== med) : [...cur, med];
                                                            setR(groupKey, next);
                                                        }}
                                                        style={{
                                                            padding:'3px 10px', borderRadius:12, fontSize:11, cursor:'pointer', userSelect:'none',
                                                            background: isChecked ? groupColor : '#fff',
                                                            color: isChecked ? '#fff' : '#555',
                                                            border: `1px solid ${isChecked ? groupColor : '#e0e0e0'}`,
                                                            fontWeight: isChecked ? 700 : 400, transition: 'all 0.12s',
                                                        }}>
                                                        {isChecked ? '✓ ' : ''}{med}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            );
        }

        if (q.type === 'matrix') return (
            <Card key={q.ref} size="small" style={{ marginBottom:8, borderLeft:`3px solid ${sColor}` }}>
                <div style={{fontSize:12,fontWeight:700,marginBottom:10}}>
                    <Tag color={sColor} style={{border:'none',color:'#fff',backgroundColor:sColor}}>Q{qNum}</Tag> {q.text}
                </div>
                <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:400}}>
                        <thead>
                            <tr style={{backgroundColor:'#f0f0f0'}}>
                                <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid #e0e0e0',minWidth:160}}></th>
                                {q.options.map(opt => (
                                    <th key={opt} style={{padding:'6px 8px',textAlign:'center',borderBottom:'1px solid #e0e0e0',color:sColor,fontSize:11}}>{opt}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {q.sub_items.map((sub,ri) => (
                                <tr key={sub} style={{backgroundColor:ri%2?'#fafafa':'#fff'}}>
                                    <td style={{padding:'7px 10px',fontWeight:600,borderBottom:'1px solid #f5f5f5'}}>{sub}</td>
                                    {q.options.map(opt => (
                                        <td key={opt} style={{textAlign:'center',borderBottom:'1px solid #f5f5f5',padding:'5px'}}>
                                            <CircleSelect selected={getR(q.ref,sub)===opt} onClick={()=>setR(q.ref,opt,sub)} color={sColor} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        );

        return (
            <Card key={q.ref} size="small" style={{ marginBottom:8, borderLeft:`3px solid ${sColor}` }}>
                <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>
                    <Tag color={sColor} style={{border:'none',color:'#fff',backgroundColor:sColor}}>Q{qNum}</Tag> {q.text}
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                    {q.options.map(opt => <OptionBtn key={opt} label={opt} selected={getR(q.ref)===opt} onClick={()=>setR(q.ref,opt)} color={sColor} />)}
                </div>
            </Card>
        );
    };

    return (
        <div style={{fontFamily:'Arial,sans-serif'}}>
            {/* Banner */}
            <div style={{background:`linear-gradient(135deg,${accentColor},${isIP?'#0050b3':'#00a651'})`,borderRadius:8,padding:'12px 18px',marginBottom:14,color:'#fff'}}>
                <div style={{fontWeight:800,fontSize:14}}>CUSTOMER SATISFACTION SURVEY — {isIP?'INPATIENT':'OUTPATIENT'}</div>
                <div style={{fontSize:11,opacity:0.85,marginTop:3}}>Ministry of Health · National Quality Management Program · Please TICK your answer to each question.</div>
            </div>

            {/* Progress */}
            <div style={{marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                    <span style={{fontSize:11,color:'#888'}}>Completion</span>
                    <span style={{fontSize:11,fontWeight:700,color:scoreColor(completion)}}>{completion}%</span>
                </div>
                <Progress percent={completion} showInfo={false} strokeColor={scoreColor(completion)} size="small" />
            </div>

            {/* Header fields */}
            <Card size="small" style={{marginBottom:12,backgroundColor:'#fafafa'}}>
                <Row gutter={12}>
                    <Col span={12}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>NAME OF FACILITY *</label>
                        <AntdSelect showSearch options={facilities} value={hdr.facility_id}
                            onChange={v => {
                                const fac = facilities.find(f => f.id === v);
                                // Parse services_offered so survey type awareness works
                                const svcs = parseServices(fac?.services_offered);
                                setHdr(h => ({
                                    ...h,
                                    facility_id:   v,
                                    region_manual: fac?.region || fac?.located_region || h.region_manual || '',
                                    fac_services:  svcs,
                                }));
                            }}
                            style={{width:'100%'}} />
                    </Col>
                    <Col span={6}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>DATE</label>
                        <AntdDatepicker value={hdr.survey_date} onChange={v=>setHdr({...hdr,survey_date:v})} style={{width:'100%'}} />
                    </Col>
                    <Col span={8}>
                        <label style={{fontWeight:600,display:'block',marginBottom:4}}>Assessment Period</label>
                        <Select style={{width:'100%'}}
                            value={hdr.period_label || undefined}
                            placeholder="Select period"
                            onChange={v=>setHdr({...hdr,period_label:v})}>
                            {(periods||[]).map(p=>(
                                <Select.Option key={p.label} value={p.label}>
                                    {p.is_current==1?'★ ':''}{p.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>{isIP?'WARD ADMITTED IN':'DEPARTMENTS VISITED'}</label>
                        <Input value={isIP?hdr.ward_admitted:hdr.departments_visited}
                            onChange={e=>setHdr({...hdr,[isIP?'ward_admitted':'departments_visited']:e.target.value})} />
                    </Col>
                    <Col span={6} style={{marginTop:8}}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>ARRIVAL TIME</label>
                        <Input type="time" value={hdr.arrival_time} onChange={e=>setHdr({...hdr,arrival_time:e.target.value})} />
                    </Col>
                    <Col span={6} style={{marginTop:8}}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>DEPARTURE TIME</label>
                        <Input type="time" value={hdr.departure_time} onChange={e=>setHdr({...hdr,departure_time:e.target.value})} />
                    </Col>
                    <Col span={12} style={{marginTop:8}}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>REGION</label>
                        <Input placeholder="Auto-filled from facility" value={hdr.region_manual} onChange={e=>setHdr({...hdr,region_manual:e.target.value})} />
                    </Col>
                </Row>
            </Card>

            {/* Patient demographics */}
            <Card size="small" style={{marginBottom:12,backgroundColor:'#fafafa'}}>
                <Row gutter={12}>
                    <Col span={6}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>PATIENT GENDER</label>
                        <Radio.Group value={hdr.patient_gender} onChange={e=>setHdr({...hdr,patient_gender:e.target.value})} size="small">
                            <Radio.Button value="Male">Male</Radio.Button>
                            <Radio.Button value="Female">Female</Radio.Button>
                            <Radio.Button value="Other">Other</Radio.Button>
                        </Radio.Group>
                    </Col>
                    <Col span={4}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>AGE (years)</label>
                        <Input type="number" min="0" max="120" placeholder="e.g. 34"
                            value={hdr.patient_age}
                            onChange={e=>setHdr({...hdr,patient_age:e.target.value})}
                            style={{width:'100%'}}/>
                    </Col>
                    <Col span={6}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>PAID FOR SERVICES?</label>
                        <Radio.Group value={hdr.paid_for_service} onChange={e=>setHdr({...hdr,paid_for_service:e.target.value})} size="small">
                            <Radio.Button value="Yes">Yes</Radio.Button>
                            <Radio.Button value="No">No</Radio.Button>
                        </Radio.Group>
                    </Col>
                    {hdr.paid_for_service === 'Yes' && (
                        <Col span={6}>
                            <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>AMOUNT PAID (E)</label>
                            <Input type="number" min="0" placeholder="e.g. 50"
                                value={hdr.amount_paid}
                                onChange={e=>setHdr({...hdr,amount_paid:e.target.value})}
                                style={{width:'100%'}}
                                prefix="E"/>
                        </Col>
                    )}
                </Row>
            </Card>

            {/* Questions by section */}
            {sections.map(section => {
                const qs = questions.filter(q=>q.section===section);
                return (
                    <div key={section}>
                        <SectionHdr title={section} />
                        {qs.map(q => renderQuestion(q))}
                        {(section==='ATTITUDE'||section==='INTERPERSONAL RELATIONS/ATTITUDES OF STAFF') && (
                            <Card size="small" style={{marginBottom:8,backgroundColor:'#fefefe'}}>
                                <label style={{fontSize:11,fontWeight:700,color:'#888'}}>COMMENTS:</label>
                                <Input style={{marginTop:4}} placeholder="Optional comments..." value={hdr.attitude_comments}
                                    onChange={e=>setHdr({...hdr,attitude_comments:e.target.value})} />
                            </Card>
                        )}
                        {section==='SUPPLIES AND COMMODITIES' && isIP && (
                            <Card size="small" style={{marginBottom:8,backgroundColor:'#fefefe'}}>
                                <label style={{fontSize:11,fontWeight:700,color:'#888'}}>COMMENTS:</label>
                                <Input style={{marginTop:4}} placeholder="Optional comments..." value={hdr.supplies_comments}
                                    onChange={e=>setHdr({...hdr,supplies_comments:e.target.value})} />
                            </Card>
                        )}
                    </div>
                );
            })}

            {/* Final comments */}
            <Card size="small" style={{backgroundColor:'#f9f9f9',marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:11,marginBottom:5}}>
                    THANK YOU FOR YOUR PARTICIPATION. IF YOU HAVE ANY SUGGESTIONS OR COMMENTS PLEASE WRITE THEM BELOW:
                </div>
                <TextArea rows={3} value={comments} onChange={e=>setComments(e.target.value)} placeholder="Your suggestions or comments..." />
            </Card>

            <div style={{display:'flex',justifyContent:'flex-end',gap:10,paddingTop:6}}>
                <Button onClick={onClose}>Cancel</Button>
                <Button type="primary" loading={loading} onClick={save} disabled={!hdr.facility_id}
                    style={{backgroundColor:accentColor}}>
                    Submit Survey
                </Button>
            </div>
        </div>
    );
}

// ─── SURVEY DETAIL ────────────────────────────────────────────────────────────
function SurveyDetail({ survey }) {
    if (!survey) return null;
    const questions = survey.survey_type==='inpatient' ? IP_QUESTIONS : OP_QUESTIONS;
    const rm = {};
    (survey.responses||[]).forEach(r => {
        rm[r.sub_item ? `${r.question_ref}__${r.sub_item}` : r.question_ref] = { v:r.response_value, s:parseFloat(r.numeric_score||0) };
    });
    const sections = [...new Set(questions.map(q=>q.section))];

    return (
        <div>
            <Row gutter={16} style={{marginBottom:12}}>
                <Col span={9}><strong>Facility:</strong> {survey.facility_name}</Col>
                <Col span={7}><strong>Date:</strong> {moment(survey.survey_date).format('DD MMM YYYY')}</Col>
                <Col span={8}>
                    <strong>Score: </strong>
                    <span style={{fontWeight:800,fontSize:17,color:scoreColor(parseFloat(survey.overall_score||0))}}>
                        {parseFloat(survey.overall_score||0).toFixed(1)}%
                    </span>
                </Col>
                {survey.patient_gender && <Col span={6} style={{marginTop:6}}><strong>Gender:</strong> {survey.patient_gender}</Col>}
                {survey.patient_age    && <Col span={4} style={{marginTop:6}}><strong>Age:</strong> {survey.patient_age} yrs</Col>}
                {survey.paid_for_service && <Col span={8} style={{marginTop:6}}>
                    <strong>Payment:</strong> {survey.paid_for_service}
                    {survey.paid_for_service === 'Yes' && survey.amount_paid &&
                        <span style={{marginLeft:6,color:'#fa8c16',fontWeight:700}}>E{survey.amount_paid}</span>}
                </Col>}
                {survey.arrival_time && <Col span={6} style={{marginTop:6}}><strong>Arrival:</strong> {survey.arrival_time}</Col>}
                {survey.departure_time && <Col span={6} style={{marginTop:6}}><strong>Departure:</strong> {survey.departure_time}</Col>}
                {survey.departments_visited && <Col span={12} style={{marginTop:6}}><strong>Departments:</strong> {survey.departments_visited}</Col>}
                {survey.ward_admitted && <Col span={12} style={{marginTop:6}}><strong>Ward:</strong> {survey.ward_admitted}</Col>}
                {survey.region && <Col span={8} style={{marginTop:6}}><strong>Region:</strong> {survey.region}</Col>}
                {survey.attitude_comments && <Col span={24} style={{marginTop:6}}><strong>Attitude Comments:</strong> {survey.attitude_comments}</Col>}
                {survey.supplies_comments && <Col span={24} style={{marginTop:6}}><strong>Supplies Comments:</strong> {survey.supplies_comments}</Col>}
            </Row>
            {sections.map(section => {
                const qs = questions.filter(q=>q.section===section);
                return (
                    <div key={section}>
                        <div style={{backgroundColor:SECTION_COLORS[section]||'#1890ff',color:'#fff',fontWeight:700,fontSize:11,padding:'4px 10px',borderRadius:4,margin:'12px 0 6px'}}>{section}</div>
                        {qs.map(q => {
                            if (q.type==='matrix') return (
                                <div key={q.ref} style={{marginBottom:8}}>
                                    <div style={{fontSize:11,color:'#555',marginBottom:4}}><Tag color="blue" size="small">{q.ref}</Tag> {q.text}</div>
                                    {q.sub_items.map(sub => {
                                        const r=rm[`${q.ref}__${sub}`];
                                        return r ? (
                                            <div key={sub} style={{display:'flex',alignItems:'center',gap:10,padding:'2px 0 2px 18px'}}>
                                                <span style={{width:180,fontSize:11,color:'#888'}}>{sub}</span>
                                                <Tag style={{fontSize:10}}>{r.v}</Tag>
                                                <span style={{fontSize:11,color:scoreColor(r.s),fontWeight:600}}>{r.s.toFixed(0)}</span>
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            );
                            const r=rm[q.ref];
                            const notRec = (survey.responses||[]).filter(x => x.question_ref && x.question_ref.startsWith(q.ref + '__notreceived__'));
                            return (
                                <div key={q.ref} style={{borderBottom:'1px solid #f5f5f5',padding:'4px 0'}}>
                                    <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
                                        <Tag color="blue" size="small" style={{flexShrink:0,fontSize:10}}>{q.ref}</Tag>
                                        <span style={{fontSize:11,color:'#555',flex:1,lineHeight:1.5}}>{q.text}</span>
                                        {r
                                            ? <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                                                <Tag style={{fontSize:10}}>{r.v}</Tag>
                                                <span style={{fontSize:11,color:scoreColor(r.s),fontWeight:700}}>{r.s.toFixed(0)}</span>
                                              </div>
                                            : <Tag color="default" style={{fontSize:10}}>—</Tag>
                                        }
                                    </div>
                                    {notRec.length > 0 && (
                                        <div style={{marginTop:4,marginLeft:20,padding:'6px 10px',background:'#fff7e6',borderRadius:6,border:'1px solid #ffd591'}}>
                                            <div style={{fontSize:10,fontWeight:700,color:'#d46b08',marginBottom:4}}>Medications not received:</div>
                                            {notRec.map(nr => (
                                                <div key={nr.question_ref} style={{fontSize:11,color:'#555'}}>
                                                    <strong>{nr.sub_item}:</strong> {nr.response_value}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
            {survey.comments && (
                <div style={{marginTop:12,padding:10,backgroundColor:'#fafafa',borderRadius:6,fontSize:11,color:'#555'}}>
                    <strong>Comments:</strong> {survey.comments}
                </div>
            )}
        </div>
    );
}


// ─── SURVEY ANALYTICS PANEL ──────────────────────────────────────────────────
function SurveyAnalyticsPanel({ filters, periods, facilities }) {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading]     = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [aFilters, setAFilters]   = useState({});
    const [medFilter, setMedFilter] = useState(null);

    useEffect(() => {
        if (!collapsed) loadAnalytics();
    }, [collapsed]);

    const loadAnalytics = async (f = aFilters) => {
        setLoading(true);
        try {
            const res = await QoCService.getSurveyAnalytics(f);
            setAnalytics(res.data?.data || null);
        } catch { message.error('Failed to load analytics'); }
        finally { setLoading(false); }
    };

    const SECTIONS   = ['TIMELINESS','ATTITUDE','FACILITY','SUPPLIES','PATIENT RIGHTS','OVERALL'];
    const SEC_COLORS = {'TIMELINESS':'#0ea5e9','ATTITUDE':'#8b5cf6','FACILITY':'#10b981','SUPPLIES':'#f59e0b','PATIENT RIGHTS':'#ef4444','OVERALL':'#6366f1'};
    const SAT_LABELS = {'very_satisfied':'Very Satisfied','satisfied':'Satisfied','unsatisfied':'Unsatisfied','very_unsatisfied':'Very Unsatisfied'};
    const SAT_COLORS = {'very_satisfied':'#52c41a','satisfied':'#a0d911','unsatisfied':'#fa8c16','very_unsatisfied':'#f5222d'};

    const facilityChart = () => {
        if (!analytics?.by_facility?.length) return null;
        const facs = [...new Set(analytics.by_facility.map(r => r.facility_name))];
        const ipData = facs.map(f => { const r = analytics.by_facility.find(x => x.facility_name===f && x.survey_type==='inpatient'); return r ? parseFloat(r.avg_score) : null; });
        const opData = facs.map(f => { const r = analytics.by_facility.find(x => x.facility_name===f && x.survey_type==='outpatient'); return r ? parseFloat(r.avg_score) : null; });
        return { facs, ipData, opData };
    };

    const sectionData = () => {
        const ip = analytics?.by_section?.inpatient  || {};
        const op = analytics?.by_section?.outpatient || {};
        return {
            ip: SECTIONS.map(s => ip[s] ?? 0),
            op: SECTIONS.map(s => op[s] ?? 0),
        };
    };

    const satData = (type) => {
        const rows = (analytics?.satisfaction || []).filter(r => r.survey_type === type);
        return Object.keys(SAT_LABELS).map(k => ({
            name: SAT_LABELS[k],
            value: parseInt(rows.find(r => r.overall_satisfaction === k)?.cnt || 0),
            color: SAT_COLORS[k],
        })).filter(x => x.value > 0);
    };

    const medData = () => {
        const meds = analytics?.medications || [];
        const medMap = {};
        meds.forEach(r => {
            const items = r.medications.split('|');
            items.forEach(med => {
                if (!medMap[med]) medMap[med] = { ip:0, op:0, facilities:{} };
                if (r.survey_type === 'inpatient') medMap[med].ip += parseInt(r.cnt);
                else medMap[med].op += parseInt(r.cnt);
                medMap[med].facilities[r.facility_name] = (medMap[med].facilities[r.facility_name]||0) + parseInt(r.cnt);
            });
        });
        return Object.entries(medMap).sort((a,b)=>(b[1].ip+b[1].op)-(a[1].ip+a[1].op)).slice(0,15);
    };

    const fc = facilityChart();
    const sd = sectionData();
    const md = medData();

    return (
        <div style={{marginBottom:16}}>
            <div onClick={() => setCollapsed(!collapsed)} style={{
                background:'linear-gradient(135deg,#003580,#006837)', color:'#fff',
                padding:'12px 20px', borderRadius: collapsed ? 8 : '8px 8px 0 0',
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between',
                userSelect:'none',
            }}>
                <div style={{fontWeight:700, fontSize:14}}>📊 Survey Analytics Dashboard</div>
                <div style={{fontSize:12, opacity:0.8}}>{collapsed ? '▼ Click to expand' : '▲ Click to collapse'}</div>
            </div>

            {!collapsed && (
                <div style={{border:'1px solid #e8e8e8', borderTop:'none', borderRadius:'0 0 8px 8px', padding:16, background:'#fafafa'}}>
                    <div style={{display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'flex-end'}}>
                        <div style={{minWidth:200}}>
                            <label style={{fontWeight:600, display:'block', marginBottom:4, fontSize:12}}>Facility</label>
                            <Select allowClear placeholder="All Facilities" style={{width:'100%'}} value={aFilters.facility_id}
                                onChange={v=>{const f={...aFilters,facility_id:v};setAFilters(f);loadAnalytics(f);}}>
                                {facilities.map(f=><Select.Option key={f.id} value={f.id}>{f.name}</Select.Option>)}
                            </Select>
                        </div>
                        <div style={{minWidth:180}}>
                            <label style={{fontWeight:600, display:'block', marginBottom:4, fontSize:12}}>Period</label>
                            <Select allowClear placeholder="All Periods" style={{width:'100%'}} value={aFilters.period_label}
                                onChange={v=>{const f={...aFilters,period_label:v};setAFilters(f);loadAnalytics(f);}}>
                                {periods.map(p=><Select.Option key={p.label} value={p.label}>{p.is_current==1?'★ ':''}{p.label}</Select.Option>)}
                            </Select>
                        </div>
                        <Button onClick={()=>loadAnalytics(aFilters)} loading={loading} type="primary" size="small">Refresh</Button>
                    </div>

                    {loading && <div style={{textAlign:'center',padding:40}}><Spin/></div>}

                    {!loading && analytics && (
                        <div>
                            {/* Score by Facility */}
                            {fc && (
                                <div style={{background:'#fff',borderRadius:8,padding:16,marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                                    <div style={{fontWeight:700,marginBottom:12,fontSize:15}}>📍 Average Score by Facility</div>
                                    <div style={{display:'flex',gap:16,alignItems:'flex-start'}}>
                                        {/* Inpatient */}
                                        <div style={{flex:'1 1 0',minWidth:0}}>
                                            <div style={{fontWeight:600,fontSize:13,color:'#003580',marginBottom:8}}>🛏 Inpatient</div>
                                            <div style={{overflowX:'auto'}}>
                                                <div style={{display:'flex',gap:8,paddingBottom:8}}>
                                                    {fc.facs.map((fac,i)=>({fac,val:fc.ipData[i]}))
                                                        .filter(x=>x.val!==null)
                                                        .sort((a,b)=>b.val-a.val)
                                                        .map(({fac,val})=>(
                                                        <div key={fac} style={{flex:'0 0 80px',textAlign:'center'}}>
                                                            <div style={{fontSize:10,color:'#333',fontWeight:600,marginBottom:4,height:32,overflow:'hidden',lineHeight:'1.2',display:'flex',alignItems:'flex-end',justifyContent:'center',textAlign:'center'}}>{fac}</div>
                                                            <div style={{display:'flex',justifyContent:'center',alignItems:'flex-end',height:130}}>
                                                                <div style={{width:38,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                                                                    <span style={{fontSize:11,fontWeight:700,color:scoreColor(val)}}>{val}%</span>
                                                                    <div style={{width:38,height:Math.max(4,val*1.2)+'px',background:'#003580',borderRadius:'4px 4px 0 0'}}/>
                                                                </div>
                                                            </div>
                                                            <div style={{height:1,background:'#e8e8e8',marginTop:2}}/>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            {!fc.ipData.some(x=>x!==null) && <div style={{color:'#aaa',fontSize:12}}>No inpatient data</div>}
                                        </div>
                                        {/* Outpatient */}
                                        <div style={{flex:'1 1 0',minWidth:0}}>
                                            <div style={{fontWeight:600,fontSize:13,color:'#006837',marginBottom:8}}>🏢 Outpatient</div>
                                            <div style={{overflowX:'auto'}}>
                                                <div style={{display:'flex',gap:8,paddingBottom:8}}>
                                                    {fc.facs.map((fac,i)=>({fac,val:fc.opData[i]}))
                                                        .filter(x=>x.val!==null)
                                                        .sort((a,b)=>b.val-a.val)
                                                        .map(({fac,val})=>(
                                                        <div key={fac} style={{flex:'0 0 80px',textAlign:'center'}}>
                                                            <div style={{fontSize:10,color:'#333',fontWeight:600,marginBottom:4,height:32,overflow:'hidden',lineHeight:'1.2',display:'flex',alignItems:'flex-end',justifyContent:'center',textAlign:'center'}}>{fac}</div>
                                                            <div style={{display:'flex',justifyContent:'center',alignItems:'flex-end',height:130}}>
                                                                <div style={{width:38,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                                                                    <span style={{fontSize:11,fontWeight:700,color:scoreColor(val)}}>{val}%</span>
                                                                    <div style={{width:38,height:Math.max(4,val*1.2)+'px',background:'#006837',borderRadius:'4px 4px 0 0'}}/>
                                                                </div>
                                                            </div>
                                                            <div style={{height:1,background:'#e8e8e8',marginTop:2}}/>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            {!fc.opData.some(x=>x!==null) && <div style={{color:'#aaa',fontSize:12}}>No outpatient data</div>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Score by Section */}
                            <div style={{background:'#fff',borderRadius:8,padding:16,marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                                <div style={{fontWeight:700,marginBottom:12,fontSize:13}}>📋 Score by Section</div>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                                    {['inpatient','outpatient'].map((type,ti) => (
                                        <div key={type}>
                                            <div style={{fontWeight:600,fontSize:14,color:type==='inpatient'?'#003580':'#006837',marginBottom:8}}>
                                                {type==='inpatient'?'🛏 Inpatient':'🏢 Outpatient'}
                                            </div>
                                            {SECTIONS.map((sec,i) => {
                                                const val = ti===0 ? sd.ip[i] : sd.op[i];
                                                return (
                                                    <div key={sec} style={{marginBottom:6}}>
                                                        <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:2}}>
                                                            <span style={{color:SEC_COLORS[sec],fontWeight:600}}>{sec}</span>
                                                            <span style={{fontWeight:700,color:scoreColor(val)}}>{val}%</span>
                                                        </div>
                                                        <Progress percent={val} showInfo={false} strokeColor={SEC_COLORS[sec]} size="small"/>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Satisfaction Distribution */}
                            <div style={{background:'#fff',borderRadius:8,padding:16,marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                                <div style={{fontWeight:700,marginBottom:12,fontSize:13}}>😊 Overall Satisfaction Distribution</div>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                                    {['inpatient','outpatient'].map(type => {
                                        const data = satData(type);
                                        const total = data.reduce((a,b)=>a+b.value,0);
                                        return (
                                            <div key={type}>
                                                <div style={{fontWeight:600,fontSize:14,color:type==='inpatient'?'#003580':'#006837',marginBottom:8}}>
                                                    {type==='inpatient'?'🛏 Inpatient':'🏢 Outpatient'}
                                                </div>
                                                {total===0 ? <div style={{color:'#aaa',fontSize:11}}>No data</div>
                                                    : data.map(d=>(
                                                        <div key={d.name} style={{marginBottom:6}}>
                                                            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:2}}>
                                                                <span style={{color:'#555'}}>{d.name}</span>
                                                                <span style={{fontWeight:700}}>{d.value} ({Math.round(d.value/total*100)}%)</span>
                                                            </div>
                                                            <Progress percent={Math.round(d.value/total*100)} showInfo={false} strokeColor={d.color} size="small"/>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
{/* Medications Not Received */}
<div style={{background:'#fff',borderRadius:8,padding:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:15}}>💊 Medications Not Received</div>
        <Select allowClear placeholder="Filter by medication" style={{width:260}}
            value={medFilter} onChange={v=>setMedFilter(v)}>
            {md.map(([med])=><Select.Option key={med} value={med}>{med}</Select.Option>)}
        </Select>
    </div>
    {md.length===0
        ? <div style={{color:'#aaa',fontSize:13}}>No medication shortage data recorded yet.</div>
        : (
            <div>
                {md.filter(([med])=>!medFilter||med===medFilter).map(([med,data])=>{
                    const total=data.ip+data.op;
                    const maxTotal=Math.max(...md.map(([,d])=>d.ip+d.op));
                    return (
                        <div key={med} style={{marginBottom:12}}>
                            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4,fontWeight:600}}>
                                <span style={{color:'#d46b08'}}>{med}</span>
                                <span style={{color:'#333'}}>{total} report{total!==1?'s':''}</span>
                            </div>
                            <div style={{display:'flex',height:24,borderRadius:4,overflow:'hidden',background:'#f5f5f5',marginBottom:4}}>
                                {data.ip>0 && <div style={{width:`${(data.ip/maxTotal)*100}%`,background:'#003580',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:700}}>
                                    {data.ip}
                                </div>}
                                {data.op>0 && <div style={{width:`${(data.op/maxTotal)*100}%`,background:'#006837',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:700}}>
                                    {data.op}
                                </div>}
                            </div>
                            <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                                {Object.entries(data.facilities).sort((a,b)=>b[1]-a[1]).map(([fac,cnt])=>(
                                    <Tag key={fac} style={{fontSize:11}}>{fac} ({cnt})</Tag>
                                ))}
                            </div>
                        </div>
                    );
                })}
                <div style={{display:'flex',gap:16,fontSize:12,color:'#555',marginTop:8}}>
                    <span><span style={{display:'inline-block',width:12,height:12,background:'#003580',borderRadius:2,marginRight:4}}/>Inpatient</span>
                    <span><span style={{display:'inline-block',width:12,height:12,background:'#006837',borderRadius:2,marginRight:4}}/>Outpatient</span>
                </div>
            </div>
        )
    }
</div>
                        </div>
                    )}

                    {!loading && !analytics && (
                        <div style={{textAlign:'center',padding:40,color:'#aaa'}}>
                            <div style={{fontSize:32,marginBottom:8}}>📊</div>
                            <div>No survey data available for the selected filters.</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function QoCCustomerSurveys() {
    const { periods, currentPeriod } = usePeriods();
    const [surveys, setSurveys]       = useState([]);
    const [stats, setStats]           = useState({});
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading]       = useState(false);
    const [ipModal, setIpModal]       = useState(false);
    const [opModal, setOpModal]       = useState(false);
    const [viewModal, setViewModal]   = useState(false);
    const [viewSurvey, setViewSurvey] = useState(null);
    const [filters, setFilters]       = useState({});

    // ── Track selected facility's services to filter "Add" buttons ────────────
    const [filterFacServices, setFilterFacServices] = useState([]);
    const [facSearch, setFacSearch] = useState('');

    useEffect(() => {
        loadFacilities();
        trailPageView(TRAIL_MODULES.SURVEY, window.location.pathname);
    }, []);

    useEffect(() => {
        if (currentPeriod) {
            const f = { period_label: currentPeriod.label };
            setFilters(f);
            loadSurveys(f);
        }
    }, [currentPeriod?.label]);

    const loadFacilities = async () => {
        try {
            const res = await FacilityService.all({ status:1 });
            setFacilities(res.data.result.data.map(v => ({
                id: v.id,
                name: v.business_name,
                region: v.located_region || v.region || '',
                site_type: v.site_type,
                services_offered: v.services_offered || null,
            })));
        } catch {}
    };

    const loadSurveys = async (f=filters) => {
        setLoading(true);
        try {
            const res = await QoCService.getCustomerSurveys(f);
            const data = res.data?.data||[];
            data.sort((a,b) => parseFloat(b.overall_score||0) - parseFloat(a.overall_score||0));
            setSurveys(data);
            setStats(res.data?.stats||{});
        } catch { message.error('Failed to load surveys'); }
        finally { setLoading(false); }
    };

    const handleView = async (survey) => {
        try {
            const res = await QoCService.getSurveyDetail(survey.id);
            setViewSurvey(res.data?.data||survey);
            setViewModal(true);
        } catch (e) {
            // Still show modal with basic survey data if detail fetch fails
            setViewSurvey(survey);
            setViewModal(true);
        }
    };

    const handleDelete = async (id) => {
        try {
            const survey = surveys.find(s => s.id === id);
            await QoCService.deleteSurvey(id);
            trailDelete(TRAIL_MODULES.SURVEY, (survey?.survey_type || 'Survey') + ' #' + id, id, { facilityName: survey?.facility_name });
            message.success('Deleted');
            loadSurveys();
        } catch { message.error('Failed to delete'); }
    };

    const avg = parseFloat(stats.average_score||0);
    const satPct = stats.total_surveys > 0 ? Math.round((stats.satisfied_count/stats.total_surveys)*100) : 0;

    // Determine which "Add" buttons to show based on the facility filter selection.
    // If no facility filter is active, show both. If a facility is selected,
    // show only the survey types that facility offers.
    const filterServices = parseServices(filterFacServices);
    const showIpButton   = filterServices.length === 0 || filterServices.includes('Inpatient');
    const showOpButton   = true; // outpatient always shown

    const cols = [
        {
            title:'Facility', dataIndex:'facility_name', width:190,
            render:(n,r)=><div><div style={{fontWeight:600}}>{n}</div><div style={{fontSize:11,color:'#888'}}>{r.region}</div></div>
        },
        {
            title:'Type', dataIndex:'survey_type', width:110,
            sorter:(a,b)=>a.survey_type.localeCompare(b.survey_type),
            filters:[{text:'Inpatient',value:'inpatient'},{text:'Outpatient',value:'outpatient'}],
            onFilter:(value,record)=>record.survey_type===value,
            render:t=><Tag icon={t==='inpatient'?<UserOutlined/>:<TeamOutlined/>} color={t==='inpatient'?'blue':'green'} style={{textTransform:'capitalize'}}>{t}</Tag>
        },
        { title:'Period', dataIndex:'period_label', width:110, render:p=>p?<Tag color="blue">{p}</Tag>:<span style={{color:'#ccc'}}>—</span> },
        { title:'Date', dataIndex:'survey_date', width:150, sorter:(a,b)=>new Date(a.created_at)-new Date(b.created_at), render:(d,r)=><div><div>{moment(d).format('DD MMM YYYY')}</div><div style={{fontSize:11,color:'#888'}}>{r.created_at ? moment(r.created_at).format('HH:mm') : ''}</div></div> },
        {
            title:'Score', dataIndex:'overall_score', width:130,
            sorter:(a,b)=>parseFloat(a.overall_score)-parseFloat(b.overall_score),
            render:s=>{
                const sc=parseFloat(s||0);
                return <div>
                    <span style={{fontWeight:700,fontSize:14,color:scoreColor(sc)}}>{sc.toFixed(1)}%</span>
                    <Progress percent={sc} showInfo={false} size="small" strokeColor={scoreColor(sc)} style={{marginTop:2}}/>
                </div>;
            }
        },
        {
            title:'Gender / Age', width:110,
            render:(_,r) => (
                <div style={{fontSize:11}}>
                    {r.patient_gender && <Tag style={{fontSize:10,margin:'1px'}}>{r.patient_gender}</Tag>}
                    {r.patient_age    && <span style={{color:'#888'}}>{r.patient_age} yrs</span>}
                    {r.paid_for_service && <div style={{marginTop:2}}>
                        <Tag color={r.paid_for_service==='Yes'?'orange':'default'} style={{fontSize:9}}>
                            {r.paid_for_service==='Yes' ? `Paid E${r.amount_paid||'?'}` : 'No payment'}
                        </Tag>
                    </div>}
                </div>
            )
        },
        {
            title:'Overall', dataIndex:'overall_satisfaction', width:145,
            render:s=>{
                if(!s) return <span style={{color:'#ccc'}}>—</span>;
                const cm={very_satisfied:'success',satisfied:'processing',unsatisfied:'warning',very_unsatisfied:'error'};
                return <Tag color={cm[s]||'default'}>{s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</Tag>;
            }
        },
        {
            title:'Action', width:100, fixed:'right',
            render:(_,rec)=>(
                <Space>
                    <Button size="small" icon={<EyeOutlined/>} onClick={()=>handleView(rec)}>View</Button>
                    <Popconfirm title="Delete this survey?" onConfirm={()=>handleDelete(rec.id)}>
                        <Button size="small" danger icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const ipAccent = '#003580';
    const opAccent = '#006837';

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>Customer Satisfaction Surveys</h2></div>
            </div>
            <div className="page-pad">
                {/* Stats */}
                <Row gutter={16} style={{marginBottom:16}}>
                    {[
                        {title:'Total Surveys',      value:stats.total_surveys||0,  color:'#1890ff'},
                        {title:'Inpatient',          value:stats.inpatient_count||0, color:'#0050b3'},
                        {title:'Outpatient',         value:stats.outpatient_count||0,color:'#006837'},
                        {title:'Avg Score',          value:avg.toFixed(1)+'%',       color:scoreColor(avg), isStr:true},
                        {title:'Satisfied / Better', value:satPct+'%',               color:'#52c41a',       isStr:true},
                    ].map(s=>(
                        <Col span={s.isStr?6:4} key={s.title}>
                            <Card size="small" style={{borderTop:`3px solid ${s.color}`,textAlign:'center'}}>
                                <div style={{fontSize:12,color:'#888',marginBottom:4}}>{s.title}</div>
                                <div style={{fontSize:22,fontWeight:800,color:s.color}}>{s.value}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <SurveyAnalyticsPanel filters={filters} periods={periods} facilities={facilities} />
                <Card>
                    <div style={{marginBottom:14,display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
                        <div style={{flex:1,minWidth:160}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>Facility</label>
                            <AntdSelect allowClear placeholder="All" options={facilities} value={filters.facility_id}
                                onChange={v => {
                                    setFilters({...filters, facility_id: v});
                                    // Update services so Add buttons reflect this facility's capabilities
                                    const fac = facilities.find(f => f.id === v);
                                    setFilterFacServices(fac ? parseServices(fac.services_offered) : []);
                                }}
                                style={{width:'100%'}} />
                        </div>
                        <div style={{flex:1,minWidth:160}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>Period</label>
                            <Select allowClear placeholder="All Periods" style={{width:'100%'}}
                                value={filters.period_label||undefined}
                                onChange={v=>{const f={...filters,period_label:v||''};setFilters(f);loadSurveys(f);}}>
                                {periods.map(p=>(
                                    <Select.Option key={p.label} value={p.label}>
                                        {p.is_current==1?'★ ':''}{p.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        <div style={{minWidth:120}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>Type</label>
                            <Select allowClear placeholder="All" value={filters.survey_type}
                                onChange={v=>{const f={...filters,survey_type:v};setFilters(f);loadSurveys(f);}} style={{width:'100%'}}>
                                <Select.Option value="inpatient">Inpatient</Select.Option>
                                <Select.Option value="outpatient">Outpatient</Select.Option>
                            </Select>
                        </div>
                        <div style={{minWidth:210}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>Date Range</label>
                            <DatePicker.RangePicker
                                onChange={v=>{const f={...filters,from_date:v?.[0]?.format('YYYY-MM-DD'),to_date:v?.[1]?.format('YYYY-MM-DD')};setFilters(f);loadSurveys(f);}}
                                style={{width:'100%'}} />
                        </div>
                    </div>
                    <Table columns={cols} dataSource={surveys} rowKey="id" loading={loading}
                        pagination={{pageSize:20,showTotal:t=>`${t} surveys`}} scroll={{x:900}} />
                </Card>
            </div>

            {/* Inpatient Modal */}
            <Modal
                title={<div style={{background:`linear-gradient(135deg,${ipAccent},#0050b3)`,margin:'-20px -24px 0',padding:'13px 24px',borderRadius:'8px 8px 0 0',color:'#fff',fontWeight:700}}>
                    <UserOutlined style={{marginRight:8}}/>Inpatient Customer Satisfaction Survey (11 Questions)
                </div>}
                open={ipModal} onCancel={()=>setIpModal(false)} footer={null}
                width={820} style={{top:20}} bodyStyle={{maxHeight:'82vh',overflowY:'auto',paddingTop:18}}>
                <SurveyForm type="inpatient" facilities={facilities}
                    periods={periods} currentPeriod={currentPeriod}
                    onSaved={()=>{setIpModal(false);loadSurveys();}}
                    onClose={()=>setIpModal(false)} />
            </Modal>

            {/* Outpatient Modal */}
            <Modal
                title={<div style={{background:`linear-gradient(135deg,${opAccent},#00a651)`,margin:'-20px -24px 0',padding:'13px 24px',borderRadius:'8px 8px 0 0',color:'#fff',fontWeight:700}}>
                    <TeamOutlined style={{marginRight:8}}/>Outpatient Customer Satisfaction Survey (10 Questions)
                </div>}
                open={opModal} onCancel={()=>setOpModal(false)} footer={null}
                width={820} style={{top:20}} bodyStyle={{maxHeight:'82vh',overflowY:'auto',paddingTop:18}}>
                <SurveyForm type="outpatient" facilities={facilities}
                    periods={periods} currentPeriod={currentPeriod}
                    onSaved={()=>{setOpModal(false);loadSurveys();}}
                    onClose={()=>setOpModal(false)} />
            </Modal>

            {/* View Modal */}
            <Modal
                title={viewSurvey
                    ? `${viewSurvey.survey_type === 'inpatient' ? 'Inpatient' : 'Outpatient'} Survey — ${viewSurvey.facility_name} · ${moment(viewSurvey?.survey_date).format('DD MMM YYYY')}`
                    : 'Survey'}
                open={viewModal} onCancel={()=>setViewModal(false)}
                footer={[<Button key="c" onClick={()=>setViewModal(false)}>Close</Button>]}
                width={700} bodyStyle={{maxHeight:'76vh',overflowY:'auto'}}>
                <SurveyDetail survey={viewSurvey} />
            </Modal>
        </div>
    );
}